import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

import { apiErrorFrom } from '../ScenarioBuilder/errors';

/**
 * The blueprint page's own read: what the answer key marks, asset by asset.
 *
 * ADMIN ONLY, AND THAT IS THE POINT. The per-asset reasons, the matrix column
 * names, the identity hash, the audit evidence and the refusal sentences live
 * on this endpoint and nowhere else. The trainer's launch card carries the
 * COUNTS of the same document and none of its reasons — a matrix column name
 * in front of a trainee is a leak of the marking scheme, so the two audiences
 * read two different documents rather than one document with a flag on it.
 *
 * The client goes through `apiCaller` like every other admin read, so token
 * refresh behaves identically, and through the scenario-builder error
 * flattener so a refusal on this page reads the same as a refusal on the
 * Jobs tab.
 */

/** One started-session asset: graded under a matrix column, or not, with why. */
export type AnswerKeyCoverageRow = {
  /** null for a pair PM would need at session start and does not have. */
  asset_id: number | null;
  ticker: string;
  name: string;
  /** The PM instrument class ('FX', 'AFSBond', 'SimplifiedOption', ...). */
  class: string;
  tradable: boolean;
  /** PM adds this at session start; the template does not list it. */
  injected: boolean;
  injected_for: string | null;
  status: 'graded' | 'not_graded' | string;
  /** The column of the conviction matrix that grades it. */
  matrix_column: string | null;
  /** The instrument code the answer key carries for it, when it carries one. */
  key_code: string | null;
  /** For an option, the column that grades the instrument it is written on. */
  parent_code: string | null;
  assessment_scoring?: string | null;
  reason_kind: string;
  reason: string;
  /**
   * The endpoint's own sentence about this row -- how the key grades it, or
   * where it came from. A graded row carries no `reason` and this is the whole
   * story it has; an injected pair carries both.
   */
  note?: string | null;
  /** The key's own quality audit check that names this code, when one does. */
  audit: { verdict: string; detail: string } | null;
};

/**
 * The key itself. Shaped so `readAnswerKeyGate` can read it directly: the
 * verdict word, the headline the backend authored, and the launch veto all
 * sit where that reader looks for them.
 */
export type AnswerKeyCoverageKey = {
  code: string;
  name?: string | null;
  /** 'draft' | 'active' | 'retired'. */
  status?: string | null;
  identity_hash?: string | null;
  /** false = the key was derived from different content than what is there now. */
  identity_matches?: boolean | null;
  derived_at?: string | null;
  derived_by?: string | null;
  model?: string | null;
  verdict?: unknown;
  headline?: string | null;
  pass_bar?: number | null;
  launch_blocked?: boolean | null;
  launch_block_reason?: string | null;
  retired_at?: string | null;
  superseded_by?: string | null;
};

export type AssessmentRegeneration = {
  automatic: boolean;
  status: 'queued' | 'running' | 'ready' | 'failed' | 'blocked' | 'idle';
  message?: string;
  retryable?: boolean;
  job_id?: string | null;
  requested_revision?: number;
  completed_revision?: number;
};

export type AnswerKeyCoverageDocument = {
  regeneration?: AssessmentRegeneration;
  variant_id?: number | null;
  template_session_id?: number | null;
  params_version?: string | null;
  key: AnswerKeyCoverageKey | null;
  /** Whether a launch of this variant would be allowed, and why not. */
  launch_check?: {
    would_launch?: boolean | null;
    refusal?: string | null;
    /** The refusal kind (identity_mismatch, not_audited, launch_blocked, ...). */
    code?: string | null;
  };
  /** The model a proposal run from this page would ask, or empty. */
  proposal_model?: string | null;
  counts?: {
    tradable?: number | null;
    graded?: number | null;
    not_graded?: number | null;
    injected_fx?: number | null;
    by_reason?: Record<string, number>;
  };
  rows?: AnswerKeyCoverageRow[];
  /** One sentence per way the key's identity differs from what is there now. */
  identity_difference?: string[];
  unknown_factors?: unknown[];
  problems?: unknown[];
};

/**
 * The per-asset document for ONE VARIANT, which is the only thing this
 * endpoint answers: a variant names the exercise, and the exercise is what an
 * answer key is derived from. Called without one the server answers 404
 * ("Variant not found for this blueprint"), so the page waits until it has
 * picked a variant rather than asking and rendering an error.
 */
export const fetchAnswerKeyCoverage = async (
  blueprintId: number,
  variantId?: number,
): Promise<AnswerKeyCoverageDocument> => {
  const data = variantId != null ? { variant_id: variantId } : {};
  const response = await apiCaller<typeof data>({
    path: `/blueprints/${blueprintId}/answer_key_coverage`,
    method: 'GET',
    data,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    // The STATUS travels with the sentence: 403 (not an admin) and 404 (a
    // variant that is not this blueprint's) are different things to say.
    throw apiErrorFrom(response.status, response.result);
  }
  return response.result as AnswerKeyCoverageDocument;
};

/** Retry a failed automatic update; unchanged news encodings are reused. */
export const retryAssessmentGeneration = async (
  blueprintId: number,
  variantId: number,
): Promise<{ regeneration: AssessmentRegeneration }> => {
  const response = await apiCaller<{ variant_id: number }>({
    path: `/blueprints/${blueprintId}/retry_assessment_generation`,
    method: 'POST',
    data: { variant_id: variantId },
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    throw apiErrorFrom(response.status, response.result);
  }
  return response.result as { regeneration: AssessmentRegeneration };
};

/** Read durable regeneration status without deriving identity or auditing a key. */
export const fetchAssessmentGeneration = async (
  blueprintId: number,
  variantId: number,
): Promise<{ regeneration: AssessmentRegeneration }> => {
  const response = await apiCaller<{ variant_id: number }>({
    path: `/blueprints/${blueprintId}/assessment_generation`,
    method: 'GET',
    data: { variant_id: variantId },
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    throw apiErrorFrom(response.status, response.result);
  }
  return response.result as { regeneration: AssessmentRegeneration };
};
